/* eslint-disable */

import { get, isEqual, uniqBy, cloneDeep } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import chartsContainer from '@/components/market/market-detail-charts/container/container.vue';
import chartsHeader from '@/components/market/market-detail-charts/header/header.vue';
import chartsTable from '@/components/market/market-detail-charts/table/table.vue';
import chartsTimeline from '@/components/market/market-detail-charts/timeline/timeline.vue';
import marketContractsStats from '@/components/market/market-contracts-stats/market-contracts-stats.vue';
import loaderMixin from '@/mixins/loader';
import { groupByKeepOrder, hasMarketHiddenContracts, isHighChartsReady } from '@/utils/helpers';
import { regularContractPrice } from './utils';
import { isMarketSingle } from '@/utils/helpers';
import log from '@/logger';
import contentLoaderMixin from '@/mixins/contentLoader';

// const YES_CONTRACT_TYPE = 'Yes';
// const NO_CONTRACT_TYPE = 'No';
// const NO_CONTRACT_LINE_COLOR = '#F27130';
const MARKET_SINGLE_CONTRACT_NAME = 'Closing Price';
const INITIAL_SHOW_CONTRACTS = 3;
export const INITIAL_MAX_CONTRACTS = 6;

const CHART_TYPES = {
  LINE: 'line',
  CANDLE: 'candle',
};

export default {
  mixins: [
    loaderMixin,
    contentLoaderMixin,
  ],
  props: {
    market: {
      type: Object,
      required: false,
    },
    chartOptions: {
      type: Object,
      default: null,
    },
    theme: {
      type: String,
      default: '',
    },
    cache: {
      type: Boolean,
      default: false,
    },
    chartSystemOptions: {
      type: Object,
      default: () => {},
    },
    suppressGlobalLoader: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const contractIds = get(this.chartOptions, 'contractIds', []) || [];

    return {
      hasMarketHiddenContracts,
      initialised: false,
      chartType: CHART_TYPES.LINE,
      preparedContractIds: null,
      toggled: false,
      disabledContracts: new Set(),
      availableContractIds: contractIds,
      chartShowContractsInitial: contractIds.length > 0 ? contractIds.length : INITIAL_SHOW_CONTRACTS,
      chartMaxContracts: contractIds.length > 0 ? null : INITIAL_MAX_CONTRACTS,
      chartTimeline: null,
      chartDataLoaded: false,
      chartData: [],
      originalChartData: [],
      showStats: false,
      showVolume: false,
    };
  },
  components: {
    chartsContainer,
    chartsHeader,
    chartsTable,
    chartsTimeline,
    marketContractsStats,
  },
  computed: {
    ...mapGetters('app', ['isLayoutDesktop', 'isLayoutMobile']),
    ...mapState('market', ['marketContractsStats']),
    classModifiers() {
      return {
        'market-detail-charts': true,
        [`market-detail-charts--${this.theme}`]: this.theme,
      };
    },
    title() {
      return get(this.chartOptions, 'chartTitle', null);
    },
    caption() {
      return get(this.chartOptions, 'chartCaption', null);
    },
    chartContracts() {
      const results = [];
      const contractIdDataGroup = groupByKeepOrder(this.chartData, 'contractId');

      // show contracts that represent selected point on time on the chart
      contractIdDataGroup.forEach((data) => {
        results.push(data[data.length - 1]);
      });

      const marketContracts = cloneDeep(this.market.contracts || this.market.marketContracts);
      return results.map((item) => {
        const contract = marketContracts.find(i => i.contractId === item.contractId) || {};
        return {
          ...item,
          contractImageUrl: contract.contractImageUrl,
          contractImageSmallUrl: contract.contractImageSmallUrl,
        };
      })
    },
    showChartsTable() {
      const noChartOptionOrLegendOn = this.chartOptions === null || get(this.chartOptions, 'includeLegend', false);
      return this.chartContracts.length > 1 && noChartOptionOrLegendOn;
    },
    filteredChartData() {
      return this.chartData.filter(el => el.checked);
    },
  },
  methods: {
    ...mapActions('chart', ['getMarketChartData']),
    ...mapActions('market', [
      'getMarketContractsStats', 'clearMarketContractsStats',
    ]),
    prepareDisabledContracts(data) {
      if (this.availableContractIds.length > 0) {
        this.disabledContracts = new Set(uniqBy(data, el => el.contractId).map(el => el.contractId).filter(i => !this.availableContractIds.includes(i)))
      } else {
        const results = [];
        const contractIdDataGroup = groupByKeepOrder(data, 'contractId');
        // show contracts that represent selected point on time on the art
        contractIdDataGroup.forEach((groupData) => {
          results.push(groupData[groupData.length - 1]);
        });

        results.forEach((el, index) => {
          if (index >= this.chartShowContractsInitial) {
            this.disabledContracts.add(el.contractId);
          }
        });
      }
    },
    prepareChartData(source) {
      const data = cloneDeep(source);
      const contractIds = this.availableContractIds.length > 0 ?
        this.availableContractIds :
        uniqBy(data, el => el.contractId).map(el => el.contractId);

      const isYesNoMarket = isMarketSingle(this.market);
      if (!isEqual(contractIds, this.preparedContractIds)) {
        this.prepareDisabledContracts(data);
        this.preparedContractIds = contractIds;
      }

      data.forEach((el) => {
        el.additional = false;
        el.checked = this.checkIsContractEnabled(el);
        el.chartPrice = regularContractPrice(el);

        if (isYesNoMarket) {
          el.contractName = MARKET_SINGLE_CONTRACT_NAME;
        }
      });

      return data;
    },
    checkIsContractEnabled(el) {
      return !this.disabledContracts.has(el.contractId);
    },
    getChartLoaderTarget() {
      return this.$refs.chartLoadingScope || this.$el;
    },
    beginChartRequestLoad() {
      if (this.suppressGlobalLoader) {
        this.loaderAddLegacy(this.getChartLoaderTarget());
        return;
      }
      this.loaderAdd();
    },
    endChartRequestLoad() {
      if (this.suppressGlobalLoader) {
        this.loaderRemoveLegacy(this.getChartLoaderTarget());
        return;
      }
      this.loaderRemove();
    },
    loadStats() {
      this.beginChartRequestLoad();
      this.getMarketContractsStats(this.market.marketId)
        .then(() => this.endChartRequestLoad())
        .catch(() => this.endChartRequestLoad());
    },
    loadChartData({ useOriginalData = false }) {
      if (useOriginalData) {
        this.chartData = this.prepareChartData(this.originalChartData);
      } else {
        const params = {
          marketId: this.market.marketId,
          maxContracts: this.chartMaxContracts,
          timespan: this.chartTimeline,
        };
        const storyId = get(this.chartOptions, 'cmsStoryId', null);
        if (storyId) {
          params.storyId = storyId;
          params.chartStoryVersionId = get(this.chartOptions, 'cmsVersionId', null);
          params.regionUUID = get(this.chartOptions, 'regionUUID', null);
          params.sectionType = get(this.chartOptions, 'sectionType', null);
        }
        this.beginChartRequestLoad();
        this.getMarketChartData({ params, cache: this.cache })
          .then((data) => {
            this.originalChartData = [...data];
            this.chartData = this.prepareChartData(this.originalChartData);
            this.endChartRequestLoad();
            if (this.chartData.length === 0 && !this.initialised) {
              this.$emit('no-data')
            }
            this.chartDataLoaded = true;
            this.isComponentReady = isHighChartsReady();
            this.initialised = true

          }).catch((e) => {
            console.error('Chart error', e);
            this.initialised = true;
            this.endChartRequestLoad();
        });
      }
    },
    onShowKey() {
      this.toggled = true;
    },
    volumeChartToggle(value) {
      this.showVolume = value;
    },
    onTimelineChange(timeline) {
      if (timeline === 'stats') {
        this.showStats = true;
        this.loadStats();
      } else {
        this.showStats = false;
        this.chartTimeline = timeline;
        this.loadChartData({});
      }
    },
    onTypeChange(type) {
      this.chartType = type;

      if ((type === CHART_TYPES.CANDLE) && this.chartData) {
        const contracts = uniqBy(this.chartData, el => el.contractId);
        const enabled = contracts.find(i => this.checkIsContractEnabled(i)) || contracts[0];
        const contractIds = contracts
          .map(i => i.contractId)
          .filter(i => i !== enabled.contractId);
        this.disabledContracts = new Set(contractIds);
        this.loadChartData({ useOriginalData: true });
      }
    },
    onContractToggle(item, checked) {
      const contractId = item.contractId;
      // const contractsNum = Math.min(this.market.contracts.length, INITIAL_MAX_CONTRACTS);
      // const disabledContractsMaxSize = contractsNum;

      if (this.chartType === CHART_TYPES.CANDLE) {
        if (!checked) {
          this.disabledContracts.add(contractId);
        } else {
          const contractIds = uniqBy(this.chartData, el => el.contractId)
            .map(i => i.contractId)
            .filter(i => i !== contractId);
          this.disabledContracts = new Set(contractIds);
        }
      } else {
        if (!checked) {
          this.disabledContracts.add(contractId);
        } else {
          this.disabledContracts.delete(contractId);
        }
      }

      this.loadChartData({ useOriginalData: true });
    },
  },
  created() {
    log.info(`[CHART] Rendering chart: ${JSON.stringify(this.chartOptions || {})} for ${this.market.marketId}`);
  },
  beforeDestroy() {
    this.clearMarketContractsStats();
  },
};



// WEBPACK FOOTER //
// ./src/components/market/market-detail-charts/market-detail-charts.js